import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useModal } from "@/context/ModalContext";
import { useDirty } from "@/context/DirtyContext";
import {
  createCustomerLevelProgram,
  getCustomerLevelProgram,
  getCustomerLevel,
  updateCustomerLevelProgram,
  deleteCustomerLevel,
  updateCustomerLevel,
} from "@/api/program-steps";
import { capitalizeFirstWord, safePostMessageToOpener } from "@/lib/utils";

/**
 * POST /customer-level/program/{program_id}
 * Create a customer level program
 */
export const useCreateCustomerLevelProgram = () => {
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const { setIsDirty } = useDirty();

  return useMutation({
    mutationFn: ({ programId, payload }: { programId: string; payload: object }) =>
      createCustomerLevelProgram(programId, payload),
    onSuccess: (_, { programId }) => {
      // Invalidate both query keys to ensure all related queries are refreshed
      queryClient.invalidateQueries({ queryKey: ["customer-level-program", programId] });

      // Reset dirty state to allow easy navigation
      setIsDirty({isDirt: false, confirmAction: () => {}});

      // Notify parent window to refresh its queries
      safePostMessageToOpener({
        type: "CUSTOMER_LEVEL_CREATED",
        programId: programId
      });

      showModal("done", {
        title: "Customer Level Created",
        description: "The customer level was created successfully.",
        onConfirm: () => window.close()
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error Creating Customer Level",
        description: error?.message || "An error occurred while creating the customer level.",
      });
    }
  });
};

/**
 * GET /customer-level/{customer_level_id}
 * Get customer level data
 */
export function useGetCustomerLevel(customerLevelId: string) {
  return useQuery({
    queryKey: ["customer-level"],
    queryFn: () => getCustomerLevel(customerLevelId),
    enabled: !!customerLevelId,
  });
}

/**
 * PUT /customer-level/{customer_level_id}
 * Update a customer level program
 */
export const useUpdateCustomerLevelProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerLevelId, payload }: { customerLevelId: string; payload: object }) =>
      updateCustomerLevelProgram(customerLevelId, payload),
    onSuccess: (_, { customerLevelId }) => {
      // Invalidate query to refresh UI after update
      queryClient.invalidateQueries({ queryKey: ["customer-level"] });
    },
  });
}

/**
 * GET /customer-level/program/{program_id}
 * Get customer level programs for a specific program
 */
export function useGetCustomerLevelProgram(programId: string) {
  return useQuery({
    queryKey: ["customer-level-program", programId],
    queryFn: async () => {
      const result = await getCustomerLevelProgram(programId);
      return result;
    },
    enabled: !!programId,
    staleTime: 0,
  });
}

/**
 * DELETE /customer-level/{customer_level_id}
 * Delete a customer level
 */
export const useDeleteCustomerLevelProgram = () => {
  const queryClient = useQueryClient();
  const { showModal, hideModal } = useModal();
  return useMutation({
    mutationFn: ({ programId, customerLevelId }: { programId: string; customerLevelId: string }) =>
      deleteCustomerLevel(customerLevelId),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-level"] });
      queryClient.invalidateQueries({ queryKey: ["customer-level-program", programId] });
      showModal("done", {
        title: "Deleted",
        description: "The customer level was deleted successfully.",
        onConfirm: hideModal,
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description:
          capitalizeFirstWord(error?.error || error?.message) ||
          "There was an error deleting the customer level.",
      });
    },
  });
};

/**
 * PUT /customer-level/program/{program_id}/{customer_level_id}
 * Update a customer level
 */
export const useUpdateCustomerLevel = () => {
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const { setIsDirty } = useDirty();

  return useMutation({
    mutationFn: ({ programId, customerLevelId, payload }: { programId: string; customerLevelId: string; payload: object }) =>
      updateCustomerLevel(programId, customerLevelId, payload),
    onSuccess: (_, { programId }) => {
      // Invalidate both query keys to ensure all related queries are refreshed
      queryClient.invalidateQueries({ queryKey: ["customer-level-program", programId] });

      // Reset dirty state to allow easy navigation
      setIsDirty({isDirt: false, confirmAction: () => {}});

      // Notify parent window to refresh its queries
      safePostMessageToOpener({
        type: "CUSTOMER_LEVEL_UPDATED",
        programId: programId
      });

      showModal("done", {
        title: "Success",
        description: "Customer level updated successfully",
        onConfirm: () => window.close()
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error Updating Customer Level",
        description: capitalizeFirstWord(error?.message) ||
          "An error occurred while updating the customer level.",
      });
    }
  });
};
